import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import Loader from 'components/atoms/loader';
import { canAccessPriceIntelligence } from 'services/alfa_tools';
import { bloqueioDaAssinatura } from 'services/assinatura';
import BlockedSubscription from 'components/molecules/blocked_subscription';
import BlockedSubscriptionRepresentant from 'components/molecules/blocked_subscription_representant';
import {
  fmtBRL,
  fmtDataBR,
  fmtInt,
  linkRefazerBusca,
  PERIODO_PERSONALIZADO,
  priceIntelligenceApi,
  rotuloPeriodo,
  SavedSearchListItem,
  unidadeLabel,
} from 'services/price_intelligence';
import { formatarQuantidade, totalEstimado } from 'services/price_intelligence_batch';
import AmbassadorToolFeedback from 'components/molecules/ambassador_tool_feedback';
import { PRICE_INTELLIGENCE_AMBASSADOR_SLUG } from 'services/ambassador_tools';
import { Container } from './styles';

const SEM_LOTE = '__sem_lote__';

const totalDaPesquisa = (item: SavedSearchListItem): number | null =>
  item.item && item.resumo && item.resumo.regime !== 'global'
    ? totalEstimado({
        quantidade: item.item.quantidade,
        unidade: item.item.unidade,
        resumo: { valor: item.resumo.mediana || null, unidade: item.resumo.unidade || null },
      })
    : null;

const numeroDoItem = (item: SavedSearchListItem): number => {
  const numero = item.item ? parseFloat(String(item.item.numero || '').replace(',', '.')) : NaN;
  return Number.isFinite(numero) ? numero : Number.MAX_SAFE_INTEGER;
};

const porNumeroDoItem = (a: SavedSearchListItem, b: SavedSearchListItem) =>
  numeroDoItem(a) - numeroDoItem(b);

const periodoDaPesquisa = (item: SavedSearchListItem): string | null => {
  if (item.date_from || item.date_to)
    return rotuloPeriodo({
      periodoMeses: PERIODO_PERSONALIZADO,
      dataDe: item.date_from || '',
      dataAte: item.date_to || '',
    });
  return item.period_months ? `${item.period_months} meses` : null;
};

const PriceIntelligenceSaved = () => {
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState<string>('');
  const [itens, setItens] = useState<SavedSearchListItem[]>([]);
  const [loteFiltro, setLoteFiltro] = useState<string | null>(null);
  const { profile } = useSelector<ApplicationState, ProfileState>((s) => s.storage.profile);

  const requisicaoRef = useRef<number>(0);
  const buscaInicialRef = useRef<boolean>(true);

  const carregar = async (q?: string) => {
    requisicaoRef.current += 1;
    const requisicao = requisicaoRef.current;
    setCarregando(true);
    setErro(null);
    try {
      const lista = await priceIntelligenceApi.listSaved(q);
      if (requisicao === requisicaoRef.current) setItens(lista);
    } catch (err) {
      if (requisicao === requisicaoRef.current)
        setErro('Não foi possível carregar as pesquisas salvas.');
    } finally {
      if (requisicao === requisicaoRef.current) setCarregando(false);
    }
  };

  useEffect(() => {
    document.title = 'Pesquisas de Preço Salvas - ConLicitação';
    carregar();
  }, []);

  useEffect(() => {
    if (buscaInicialRef.current) {
      buscaInicialRef.current = false;
      return undefined;
    }
    const timer = window.setTimeout(() => carregar(busca || undefined), 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const excluir = async (item: SavedSearchListItem) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir a pesquisa "${item.name}"?`)) return;
    try {
      await priceIntelligenceApi.removeSaved(item.id);
      setItens((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      setErro('Não foi possível excluir a pesquisa.');
    }
  };

  const lotes = useMemo(() => {
    const nomes: string[] = [];
    itens.forEach((i) => {
      if (i.batch_name && nomes.indexOf(i.batch_name) < 0) nomes.push(i.batch_name);
    });
    return nomes;
  }, [itens]);

  const temAvulsas = itens.some((i) => !i.batch_name);

  const grupos = useMemo(() => {
    const filtrados = itens.filter((i) => {
      if (!loteFiltro) return true;
      if (loteFiltro === SEM_LOTE) return !i.batch_name;
      return i.batch_name === loteFiltro;
    });
    const ordem: string[] = [];
    const porLote: Record<string, SavedSearchListItem[]> = {};
    filtrados.forEach((i) => {
      const chave = i.batch_name || SEM_LOTE;
      if (!porLote[chave]) {
        porLote[chave] = [];
        ordem.push(chave);
      }
      porLote[chave].push(i);
    });
    return ordem
      .sort((a, b) => (a === SEM_LOTE ? 1 : b === SEM_LOTE ? -1 : 0))
      .map((chave) => {
        const lista =
          chave === SEM_LOTE ? porLote[chave] : porLote[chave].slice().sort(porNumeroDoItem);
        const totais = lista.map(totalDaPesquisa).filter((t): t is number => t !== null);
        return {
          chave,
          nome: chave === SEM_LOTE ? 'Pesquisas avulsas' : chave,
          itens: lista,
          total: totais.reduce((soma, t) => soma + t, 0),
          itensNoTotal: totais.length,
        };
      });
  }, [itens, loteFiltro]);

  useEffect(() => {
    if (loteFiltro && loteFiltro !== SEM_LOTE && lotes.indexOf(loteFiltro) < 0) setLoteFiltro(null);
  }, [lotes, loteFiltro]);

  // Fase alfa: acesso restrito a clientes alfa_testers e clientes liberados.
  if (!canAccessPriceIntelligence(profile)) return <Redirect to="/painel" />;
  const bloqueio = bloqueioDaAssinatura(profile);
  if (bloqueio.bloqueada)
    return (
      <div className="page-container">
        {bloqueio.representada ? (
          <BlockedSubscriptionRepresentant />
        ) : (
          <BlockedSubscription paralyzed={bloqueio.paralisada} />
        )}
      </div>
    );

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions">
        <Container>
          <Link to="/pesquisa_de_precos" className="back-link">
            <i className="material-icons">arrow_back</i>
            Análise de Mercado 3.0
          </Link>

          <div className="saved-head">
            <div>
              <h4>Pesquisas salvas</h4>
              <p className="saved-sub">
                Histórico de pesquisas com valores congelados na data da busca.
              </p>
            </div>
            <Link to="/pesquisa_de_precos" className="btn btn-primary btn-ia">
              <i className="material-icons align-middle mr-1" style={{ fontSize: 18 }}>
                search
              </i>
              Nova pesquisa
            </Link>
          </div>

          <div className="saved-search-input">
            <i className="material-icons">search</i>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nome, item ou lote…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar pesquisa salva"
            />
          </div>

          {lotes.length > 0 && (
            <div className="batch-filters">
              <button
                type="button"
                className={!loteFiltro ? 'active' : ''}
                onClick={() => setLoteFiltro(null)}
              >
                Todas
              </button>
              {lotes.map((nome) => (
                <button
                  key={nome}
                  type="button"
                  className={loteFiltro === nome ? 'active' : ''}
                  onClick={() => setLoteFiltro(nome)}
                  title={nome}
                >
                  <i className="material-icons">folder_open</i>
                  {nome}
                </button>
              ))}
              {temAvulsas && (
                <button
                  type="button"
                  className={loteFiltro === SEM_LOTE ? 'active' : ''}
                  onClick={() => setLoteFiltro(SEM_LOTE)}
                >
                  Avulsas
                </button>
              )}
            </div>
          )}

          {erro && (
            <div className="alert alert-danger mt-3" role="alert">
              {erro}
            </div>
          )}

          {carregando ? (
            <div className="loading-wrap">
              <Loader />
            </div>
          ) : itens.length === 0 ? (
            <div className="empty-saved">
              <div className="empty-icon">
                <i className="material-icons">bookmark_border</i>
              </div>
              <h5>{busca ? 'Nenhuma pesquisa encontrada' : 'Nenhuma pesquisa salva ainda'}</h5>
              <p>
                {busca
                  ? 'Tente outro termo de busca.'
                  : 'Pesquise um item, selecione as amostras comparáveis e salve para guardar a referência de preço da sua proposta.'}
              </p>
              {!busca && (
                <Link to="/pesquisa_de_precos" className="btn btn-primary btn-ia">
                  Fazer minha primeira pesquisa
                </Link>
              )}
            </div>
          ) : (
            grupos.map((grupo) => (
              <div key={grupo.chave} className="saved-group">
                {(lotes.length > 0 || grupo.chave !== SEM_LOTE) && (
                  <div className="group-head">
                    <p className="group-name">
                      <i className="material-icons">
                        {grupo.chave === SEM_LOTE ? 'bookmarks' : 'folder_open'}
                      </i>
                      {grupo.nome}
                      <span>
                        {fmtInt(grupo.itens.length)}{' '}
                        {grupo.itens.length === 1 ? 'pesquisa' : 'pesquisas'}
                      </span>
                    </p>
                    {grupo.itensNoTotal > 0 && (
                      <p className="group-total">
                        Total estimado <strong>{fmtBRL(grupo.total)}</strong>
                        <span>
                          {' '}
                          ({fmtInt(grupo.itensNoTotal)} de {fmtInt(grupo.itens.length)} itens ·
                          quantidade × mediana)
                        </span>
                      </p>
                    )}
                  </div>
                )}
                <div className="saved-grid">
                  {grupo.itens.map((item) => {
                    const periodo = periodoDaPesquisa(item);
                    const unidade =
                      item.resumo && item.resumo.unidade
                        ? `/${unidadeLabel(item.resumo.unidade)}`
                        : '';
                    const total = totalDaPesquisa(item);
                    return (
                      <div key={item.id} className="saved-card">
                        <p className="saved-name" title={item.name}>
                          {item.name}
                        </p>
                        {item.query !== item.name && (
                          <p className="saved-query" title={item.query}>
                            {item.query}
                          </p>
                        )}

                        <p className="saved-median-label">Mediana de fechamento</p>
                        <p className="saved-median">
                          {item.resumo && item.resumo.mediana
                            ? `${fmtBRL(item.resumo.mediana)}${unidade}`
                            : '—'}
                        </p>
                        {item.item && item.item.quantidade !== null && (
                          <p className="saved-total">
                            {formatarQuantidade(item.item.quantidade)} {item.item.unidade || ''}
                            {total !== null ? ` · total estimado ${fmtBRL(total)}` : ''}
                          </p>
                        )}

                        <div className="saved-badges">
                          {item.resumo && (
                            <span className="badge badge-pill">
                              {fmtInt(item.resumo.n_amostras)} amostras
                            </span>
                          )}
                          {periodo && <span className="badge badge-pill">{periodo}</span>}
                          <span className="badge badge-pill">
                            {item.ufs && item.ufs.length ? item.ufs.join(', ') : 'Brasil inteiro'}
                          </span>
                        </div>

                        <div className="saved-foot">
                          <span>Salva em {fmtDataBR(item.created_at)}</span>
                          <span className="saved-actions">
                            <Link
                              to={linkRefazerBusca(item)}
                              title="Refazer busca com os mesmos filtros"
                            >
                              <i className="material-icons">refresh</i>
                            </Link>
                            <Link
                              to={`/pesquisa_de_precos/salvas/${item.id}`}
                              title="Abrir pesquisa"
                            >
                              <i className="material-icons">open_in_full</i>
                            </Link>
                            <button
                              type="button"
                              className="danger"
                              title="Excluir pesquisa"
                              onClick={() => excluir(item)}
                            >
                              <i className="material-icons">delete_outline</i>
                            </button>
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </Container>
      </div>
      <AmbassadorToolFeedback toolSlug={PRICE_INTELLIGENCE_AMBASSADOR_SLUG} />
    </div>
  );
};

export default PriceIntelligenceSaved;
